import PropTypes from 'prop-types';
import cn from 'classnames';

import figmaFileIcon from 'assets/figma_file_icon.svg';
import ReviewsUploadIcon from 'assets/ReviewsUploadIcon.svg';

import { DESIGN_AUDIT_TYPES, DESIGN_REVIEW_STATUS, FLOW_TYPES } from 'utlis/constants';
import { formatDate } from 'utlis/string_format';

import CreatedByCell from './CreatedByCell';
import NpsBadge from './NpsBadge';
import StatusBadge from './StatusBadge';

const ReviewRow = ({ review, isArchived, onView, onEdit, onRestore, isOwner, isPolling, isRestoring }) => {
  const hasFailed = !isArchived && review.review_status === DESIGN_REVIEW_STATUS.FAILURE;
  const frameName = review.extra_data?.review_frame_name || 'Review Frame';
  const isMultiFlow = review.extra_data?.flow_type === FLOW_TYPES.MULTI;
  const additionalFramesCount = review.extra_data?.review_frame_names
    ? review.extra_data.review_frame_names.length - 1
    : 0;
  const designType = review.extra_data?.design_type || DESIGN_AUDIT_TYPES.FIGMA_FRAME;
  const isScreenshot = designType == DESIGN_AUDIT_TYPES.SCREENSHOT;

  return (
    <div
      role="button"
      className={cn('group flex items-center gap-16 px-16 py-12 border-b border-gray-10', {
        'cursor-not-allowed opacity-60': hasFailed,
        'hover:bg-gray-50 cursor-pointer': !hasFailed && !isArchived,
        'bg-gray-50/50 hover:bg-gray-100 cursor-pointer': isArchived,
      })}
      onClick={() => !hasFailed && onView(review)}
    >
      <div className="w-[26px] flex-shrink-0">
        <NpsBadge review={review} />
      </div>

      <div className="flex-[4] flex items-center min-w-0">
        <div className="flex min-w-0 items-center font-inter text-sm font-medium leading-5">
          <span className="truncate text-figma-gray-900" title={frameName}>
            {frameName}
          </span>
          {isMultiFlow && additionalFramesCount > 0 && (
            <span className="pl-2 flex-shrink-0 whitespace-nowrap text-figma-gray-500">
              +
              {' '}
              {additionalFramesCount}
              {' '}
              more
            </span>
          )}
        </div>
      </div>

      {!isArchived && (
        <div className="flex-[2] flex">
          <StatusBadge status={review.review_status} />
        </div>
      )}

      <div className="flex-[2] flex items-center">
        <div className="flex items-center gap-8 ml-4">
          <img
            src={isScreenshot ? ReviewsUploadIcon : figmaFileIcon}
            alt={isScreenshot ? 'Upload' : 'Figma'}
            className="w-4 h-4"
          />
          <span className="font-inter text-sm font-medium text-figma-gray-900 leading-5">
            {isScreenshot ? 'Uploaded' : (review.extra_data?.file_name || 'Figma Frame')}
          </span>
        </div>
      </div>

      <div className="flex-[2] flex items-center">
        <span className="font-inter text-sm font-medium text-figma-gray-600 leading-5 ml-8">
          {formatDate(review.updated_at)}
        </span>
      </div>

      <div className="flex-[2] flex items-center min-w-0">
        <CreatedByCell owner={review.owner} />
      </div>

      {isArchived
        ? (
          <div className="w-16 flex-shrink-0 flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              disabled={isRestoring}
              className="font-inter text-sm font-medium text-figma-gray-600
              hover:text-figma-gray-900 transition-colors px-2 py-1 rounded hover:bg-gray-100
              disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={e => {
                e.stopPropagation();
                onRestore(review);
              }}
            >
              Restore
            </button>
          </div>
        )
        : isOwner && (
          <div className="w-12 flex-shrink-0 flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
            {!isPolling && (
              <button
                className="font-inter text-sm font-medium text-figma-gray-600
                hover:text-figma-gray-900 transition-colors px-2 py-1 rounded hover:bg-gray-100"
                onClick={e => {
                  e.stopPropagation();
                  onEdit(review);
                }}
              >
                Edit
              </button>
            )}
          </div>
        )}
    </div>
  );
};

ReviewRow.propTypes = {
  review: PropTypes.object.isRequired,
  isArchived: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onRestore: PropTypes.func,
  isOwner: PropTypes.bool,
  isPolling: PropTypes.bool,
  isRestoring: PropTypes.bool,
};

ReviewRow.defaultProps = {
  isArchived: false,
  onEdit: null,
  onRestore: null,
  isOwner: false,
  isPolling: false,
  isRestoring: false,
};

export default ReviewRow;
